import { Criticidad } from '../value-objects/Criticidad';
import { EstadoSensor } from '../value-objects/EstadoSensor';
import type { Lectura } from '../value-objects/Lectura';

/**
 * Umbral de un sensor.
 *
 * La evaluación es polimórfica: cada subtipo sabe juzgar su propia lectura.
 * Esto sustituye las cadenas `if (esDigital) … else …` que la aplicación
 * anterior repetía en cada módulo.
 */
export abstract class Umbral {
  protected constructor(
    readonly id: number,
    readonly sensorId: number,
    readonly criticidad: Criticidad,
    readonly activo: boolean,
    /** Segundos que la condición debe sostenerse antes de disparar. */
    readonly retardoSegundos: number,
    /** La alarma permanece hasta que alguien la resuelve (latched). */
    readonly retencion: boolean,
    readonly notificaciones: boolean,
    readonly actuadoresVinculados: readonly number[],
  ) {}

  /** `false` cuando la fila existe pero no tiene límites útiles configurados. */
  abstract get estaConfigurado(): boolean;

  /** Juzga una lectura concreta. No aplica retardo: eso lo decide el backend. */
  protected abstract evaluarValor(lectura: Lectura): EstadoSensor;

  /** Descripción legible del criterio, para tooltips y detalle de alarma. */
  abstract describir(unidad?: string | null): string;

  /**
   * Estado del sensor según este umbral.
   *
   * Sin lecturas no hay nada que evaluar. Con lecturas pero sin el umbral
   * terminado de configurar el sensor está normal: mide bien, simplemente
   * nadie ha dicho todavía cuándo considerarlo fuera de sitio.
   */
  evaluar(lectura: Lectura | null): EstadoSensor {
    if (!lectura) return EstadoSensor.sinDatos();
    if (!this.estaConfigurado) return EstadoSensor.normal();
    return this.evaluarValor(lectura);
  }
}

export class UmbralAnalogico extends Umbral {
  constructor(params: {
    id: number;
    sensorId: number;
    criticidad: Criticidad;
    activo: boolean;
    retardoSegundos: number;
    retencion: boolean;
    notificaciones: boolean;
    actuadoresVinculados: readonly number[];
    valorMinimo: number | null;
    valorMaximo: number | null;
    /** Banda muerta que evita que la alarma parpadee al rozar el límite. */
    histeresis: number | null;
    /** Segundos que se ignora el sensor tras arrancar el equipo. */
    inhibicionArranqueSegundos: number;
  }) {
    super(
      params.id,
      params.sensorId,
      params.criticidad,
      params.activo,
      params.retardoSegundos,
      params.retencion,
      params.notificaciones,
      params.actuadoresVinculados,
    );
    this.valorMinimo = params.valorMinimo;
    this.valorMaximo = params.valorMaximo;
    this.histeresis = params.histeresis;
    this.inhibicionArranqueSegundos = params.inhibicionArranqueSegundos;
  }

  readonly valorMinimo: number | null;
  readonly valorMaximo: number | null;
  readonly histeresis: number | null;
  readonly inhibicionArranqueSegundos: number;

  get estaConfigurado(): boolean {
    return this.valorMinimo !== null || this.valorMaximo !== null;
  }

  /**
   * El umbral analógico define una banda; salirse de ella dispara la alarma.
   * La gravedad no depende de cuánto se salga, sino de la criticidad
   * configurada para este umbral.
   */
  protected evaluarValor(lectura: Lectura): EstadoSensor {
    const v = lectura.valor;

    const fueraDeRango =
      (this.valorMinimo !== null && v < this.valorMinimo) ||
      (this.valorMaximo !== null && v > this.valorMaximo);

    if (!fueraDeRango) return EstadoSensor.normal();

    /*
     * Fuera de rango es alarma, sea cual sea la criticidad del umbral. La
     * criticidad sigue sirviendo para priorizar y para a quién se avisa, pero
     * no para el color: en pantalla o el sensor está bien o no lo está.
     */
    return EstadoSensor.alarma();
  }

  describir(unidad?: string | null): string {
    const u = unidad ? ` ${unidad}` : '';
    const { valorMinimo: min, valorMaximo: max } = this;

    if (min !== null && max !== null) return `${min}${u} – ${max}${u}`;
    if (min !== null) return `Bajo < ${min}${u}`;
    if (max !== null) return `Alto > ${max}${u}`;
    return 'Sin límites configurados';
  }
}

export class UmbralDigital extends Umbral {
  constructor(params: {
    id: number;
    sensorId: number;
    criticidad: Criticidad;
    activo: boolean;
    retardoSegundos: number;
    retencion: boolean;
    notificaciones: boolean;
    actuadoresVinculados: readonly number[];
    /** Estado en el que el sensor se considera normal. */
    estadoEsperado: boolean | null;
    /** Ventanas horarias en las que el umbral aplica; vacío = siempre. */
    horarios: readonly HorarioUmbral[];
  }) {
    super(
      params.id,
      params.sensorId,
      params.criticidad,
      params.activo,
      params.retardoSegundos,
      params.retencion,
      params.notificaciones,
      params.actuadoresVinculados,
    );
    this.estadoEsperado = params.estadoEsperado;
    this.horarios = params.horarios;
  }

  readonly estadoEsperado: boolean | null;
  readonly horarios: readonly HorarioUmbral[];

  get estaConfigurado(): boolean {
    return this.estadoEsperado !== null;
  }

  protected evaluarValor(lectura: Lectura): EstadoSensor {
    if (lectura.esEncendido === this.estadoEsperado) return EstadoSensor.normal();
    return EstadoSensor.alarma();
  }

  describir(): string {
    if (this.estadoEsperado === null) return 'Sin estado esperado configurado';
    const base = `Se espera ${this.estadoEsperado ? 'ENCENDIDO' : 'APAGADO'}`;
    return this.horarios.length ? `${base} · ${this.horarios.length} horario(s)` : base;
  }
}

export interface HorarioUmbral {
  /** 0 = domingo … 6 = sábado, siguiendo `Date.getDay()`. */
  diaSemana: number;
  /** `HH:mm` */
  horaInicio: string;
  /** `HH:mm` */
  horaFin: string;
  activo: boolean;
}
